import React from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

import { AddMinutesOrStartTerm, AddMinutesOrStartTermPlaceHolder } from './AddMinutesOrStartTerm';
import { DrawerBase } from './DrawerBase';
import { FetchingStatus } from '../../../slices/accounts/video/management/interfaces';
import { getManageMinutesList } from '../../../slices/accounts/video/management/thunk';
import { MinutesManagementTable } from './MinutesManagementTable';
import { RootState } from '../../../slices/rootReducer';
import { ScrollableContainer } from '../../credits/modals/ScrollableContainer/ScrollableContainer';
import { selectAllocationItemsList, selectListFetchingStatus } from '../../../slices/accounts/video/management/selectors';
import { selectTimeRemaining } from '../../../slices/accounts/video/dashboard/selectors';
import { TypeDispatchFunction } from '../../../tsHelpers';

import styles from './index.module.scss';

interface ExternalProps {
  onModalClose: () => void;
}

interface IMapStateToProps {
  accountId: string;
  isFetchingTheList: boolean;
  itemListCount: number;
  minutesRemaining: number;
}

interface IMapDispatchToProps {
  getManageMinutesList: TypeDispatchFunction<typeof getManageMinutesList>;
}

interface MatchParams {
  id: string;
}

type Props = IMapStateToProps & IMapDispatchToProps & ExternalProps;

interface IState {
  isEditingTable: boolean;
  isAddMinutesOrStartTermInProgress: boolean;
}

const copy = 'Set the number of minutes of video that are included in the customer’s SightX subscription. Create a new term when their annual subscription renews to reset their video minutes, or add minutes to their current term if they run out and purchase more mid-year.';
const MINUTES_REMAINING_TXT = 'minutes remaining';

class ManageMinutesDrawerBase extends React.Component<Props, IState> {

  constructor(props: Props) {
    super(props);

    this.state = {
      isEditingTable: false,
      isAddMinutesOrStartTermInProgress: false
    };

    this.handleAddMinutesOrStartTermActionCancelled = this.handleAddMinutesOrStartTermActionCancelled.bind(this);
    this.handleAddMinutesOrStartTermActionStarted = this.handleAddMinutesOrStartTermActionStarted.bind(this);
    this.handleAddMinutesOrStartTermActionSucceeded = this.handleAddMinutesOrStartTermActionSucceeded.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleTableEditionEnded = this.handleTableEditionEnded.bind(this);
    this.handleTableEditionStarted = this.handleTableEditionStarted.bind(this);
  }

  private handleModalClose() {
    const { onModalClose } = this.props;
    onModalClose();
  }

  private handleTableEditionStarted() {
    this.setState({ isEditingTable: true });
  }

  private handleTableEditionEnded() {
    this.setState({ isEditingTable: false });
  }

  private handleAddMinutesOrStartTermActionSucceeded() {
    this.props.getManageMinutesList({ accountId: this.props.accountId });
    this.setState({ isAddMinutesOrStartTermInProgress: false });
  }

  private handleAddMinutesOrStartTermActionStarted() {
    this.setState({ isAddMinutesOrStartTermInProgress: true });
  }

  private handleAddMinutesOrStartTermActionCancelled() {
    this.setState({ isAddMinutesOrStartTermInProgress: false });
  }

  private renderMinutesRemaining() {
    const { minutesRemaining } = this.props;
    if (minutesRemaining === null) {
      return null;
    }

    if (minutesRemaining === undefined) {
      return ['--', MINUTES_REMAINING_TXT].join(' ');
    }

    return [Math.trunc(minutesRemaining), MINUTES_REMAINING_TXT].join(' ');
  }

  private renderCreateTerm() {
    if(this.props.isFetchingTheList) {
      return (
        <AddMinutesOrStartTermPlaceHolder />
      );
    }

    return (
      <AddMinutesOrStartTerm
        actionStarted={this.handleAddMinutesOrStartTermActionStarted}
        actionCancelled={this.handleAddMinutesOrStartTermActionCancelled}
        actionSuceeded={this.handleAddMinutesOrStartTermActionSucceeded}
        disabled={this.state.isEditingTable}
        accountId={this.props.accountId} hasData={this.props.itemListCount > 0}
      />
    );
  }

  private renderTable() {
    return (
      <ScrollableContainer className={styles.scrollableContainer}>
        <div className={styles.tableContainer}>
          <MinutesManagementTable
            disableRowEdition={this.state.isAddMinutesOrStartTermInProgress}
            accountId={this.props.accountId}
            editionStarted={this.handleTableEditionStarted}
            editionEnded={this.handleTableEditionEnded}
          />
        </div>
      </ScrollableContainer>
    );
  }

  render() {
    return (
      <DrawerBase
        title='Manage Minutes'
        onClose={this.handleModalClose}
        className={styles.drawerBase}
      >
        <div className={styles.contents}>
          <p className={styles.copy}>{copy}</p>
          {this.renderMinutesRemaining()}
          {this.renderCreateTerm()}
          {this.renderTable()}
        </div>
      </DrawerBase>
    );
  }

}

const mapStateToProps = (state: RootState, props: RouteComponentProps<MatchParams>): IMapStateToProps => {
  const { match: { params: { id: accountId } } } = props;
  const items = selectAllocationItemsList(state);
  const listFetchingStatus = selectListFetchingStatus(state);
  const secondsRemaining = selectTimeRemaining(state);
  return {
    accountId,
    isFetchingTheList: listFetchingStatus === FetchingStatus.Loading,
    itemListCount: items.length,
    minutesRemaining: secondsRemaining / 60
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getManageMinutesList
};

const connectedManageMinutesDrawer = connect(mapStateToProps, mapDispatchToProps)(ManageMinutesDrawerBase);
const ManageMinutesDrawer = withRouter(connectedManageMinutesDrawer);

export { ManageMinutesDrawer };
